import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface VPSPlan {
  id: string;
  name: string;
  description: string;
  cpu_cores: number;
  memory_mb: number;
  disk_gb: number;
  bandwidth_gb: number;
  monthly_price: string; // Comes from database as string
  hourly_price: string;  // Comes from database as string
  features: {
    ssd_storage: boolean;
    backup_included: boolean;
    monitoring_included: boolean;
    load_balancer: boolean;
    dedicated_ip: boolean;
    priority_support?: boolean;
  };
  is_active: boolean;
}

const VPSPackagesList: React.FC = () => {
  const [packages, setPackages] = useState<VPSPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5001"}/api/vps/plans`);
      if (response.ok) {
        const data = await response.json();
        setPackages(data.data || []);
      } else {
        setError('Failed to fetch VPS plans');
      }
    } catch (error) {
      setError('Failed to fetch VPS plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPackage = (packageId: string, packageName: string) => {
    // Store selected plan in sessionStorage for the streamlined deploy flow
    const selectedPlan = packages.find(pkg => pkg.id === packageId);
    if (selectedPlan) {
      sessionStorage.setItem('selectedVPSPlan', JSON.stringify(selectedPlan));
    }
    // Redirect to the streamlined VPS deployment process
    window.location.href = `/vps/deploy`;
  };

  if (loading) {
    return (
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading VPS packages...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <p className="text-red-700">{error}</p>
              <button 
                onClick={fetchPackages}
                className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-16 shadow-xl text-center relative overflow-hidden">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent rounded-2xl"></div>
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 rounded-2xl mb-6 shadow-lg relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                <svg className="w-12 h-12 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">No VPS packages found</h3>
              <p className="text-xl text-gray-600 mb-8 max-w-md mx-auto">VPS plans will be available soon. Check back later for powerful virtual server options.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-full">
            <span className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mr-3"></span>
            <span className="text-sm font-semibold text-gray-700">VPS Plans</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">VPS</span> Plan
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Powerful Virtual Private Servers with full root access and dedicated resources. 
            Enterprise-grade infrastructure with complete control over your environment.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
          {packages.map((pkg, index) => (
            <div key={pkg.id} className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 relative overflow-hidden group">
              {/* Glossy overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              <div className="relative z-10">
                {/* Package Header */}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 rounded-2xl mb-4 shadow-lg relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                    <svg className="w-8 h-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 text-base leading-relaxed mb-6">{pkg.description}</p>
                  <div className="flex justify-center items-baseline mb-2">
                    <span className="text-4xl font-black text-primary-navy">${parseFloat(pkg.monthly_price).toFixed(2)}</span>
                    <span className="text-gray-600 text-lg ml-2">/month</span>
                  </div>
                  <div className="text-center">
                    <span className="text-sm text-gray-500">${(parseFloat(pkg.monthly_price) * 12 * 0.9).toFixed(2)}/year</span>
                    <span className="ml-2 px-2 py-1 text-xs bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full font-medium">Save 10%</span>
                  </div>
                </div>

                {/* VPS Specifications */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Server Specifications:</h4>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                      <div className="text-sm font-medium text-gray-600">CPU Cores</div>
                      <div className="text-lg font-bold text-gray-900">{pkg.cpu_cores}</div>
                    </div>
                    <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                      <div className="text-sm font-medium text-gray-600">RAM</div>
                      <div className="text-lg font-bold text-gray-900">{Math.round(pkg.memory_mb / 1024)} GB</div>
                    </div>
                    <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                      <div className="text-sm font-medium text-gray-600">SSD Storage</div>
                      <div className="text-lg font-bold text-gray-900">{pkg.disk_gb} GB</div>
                    </div>
                    <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                      <div className="text-sm font-medium text-gray-600">Bandwidth</div>
                      <div className="text-lg font-bold text-gray-900">{Math.round(pkg.bandwidth_gb / 1000)} TB</div>
                    </div>
                  </div>
                </div>

                {/* Additional Features */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Included Features:</h4>
                  <ul className="space-y-3">
                    {pkg.features.dedicated_ip && (
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm leading-relaxed">Dedicated IP Address</span>
                      </li>
                    )}
                    {pkg.features.backup_included && (
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm leading-relaxed">Daily Automated Backups</span>
                      </li>
                    )}
                    {pkg.features.monitoring_included && (
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm leading-relaxed">24/7 Server Monitoring</span>
                      </li>
                    )}
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-700 text-sm leading-relaxed">Full Root Access</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-700 text-sm leading-relaxed">Instant Deployment</span>
                    </li>
                  </ul>
                </div>

                <button
                  onClick={() => handleSelectPackage(pkg.id, pkg.name)}
                  className="w-full bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-xl"></div>
                  <span className="relative z-10">Deploy VPS</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Why Choose Us Section */}
        <div className="mt-20">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-12 shadow-xl relative overflow-hidden">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
            <div className="relative z-10">
              <div className="text-center mb-12">
                <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
                  <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
                  <span className="text-sm font-semibold text-gray-700">Why Choose Us</span>
                </div>
                
                <h3 className="text-4xl font-black text-gray-900 mb-4">
                  Why Choose <span className="text-primary-navy">12and22</span> VPS?
                </h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>,
                    title: 'Enterprise Hardware',
                    description: 'High-performance servers with SSD storage and advanced monitoring',
                    color: 'from-orange-500 via-yellow-500 to-orange-600'
                  },
                  {
                    icon: <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
                    title: 'Complete Control',
                    description: 'Full root access with ability to install any software you need',
                    color: 'from-primary-navy via-primary-800 to-primary-900'
                  },
                  {
                    icon: <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
                    title: 'Instant Setup',
                    description: 'Your VPS is deployed within minutes of order confirmation',
                    color: 'from-orange-500 via-yellow-500 to-orange-600'
                  }
                ].map((feature, index) => (
                  <div key={index} className="text-center">
                    <div className={`w-16 h-16 ${index === 0 || index === 2 ? 'bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600' : 'bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900'} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg relative overflow-hidden`}>
                      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                      <div className="relative z-10">{feature.icon}</div>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h4>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VPSPackagesList;